import * as authSelectors from "../../auth/authSelectors";
import { useSelector } from "react-redux";
import { useGetVouchers, usePunchCards } from "../../../service/api";
import { SpinnerBlock } from "../../common/Spinner";
import { FormRow } from "../../common/hook-form";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { TextAreaInput } from "../../../components/forms/inputs/TextAreaInput";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { useFormContext } from "react-hook-form";
import { CreateProductPayload, Product } from "../models/product";
import { config } from "../../../config";
import {
  cooldownRulesets_PRODUCTION,
  cooldownRulesets_STAGING,
} from "../ProductUpsert";
import { useGetProductCategories } from "../../product-categories/service/product-categories-api";
import { useHandleFetchError } from "../../../error-handler";
import { ProductTagsSelectField } from "./ProductTagsSelectField";

type Props = {
  isEdit?: boolean;
};

export const ProductInformationForm = ({ isEdit }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const methods = useFormContext<
    (CreateProductPayload | Product) & { files: File[] }
  >();

  const {
    data: productCategories,
    isLoading: isLoadingProductCategories,
    error: productCategoriesError,
  } = useGetProductCategories(organizationKeyName);

  const {
    data: voucherTypesList,
    isLoading: isLoadingVoucherTypeList,
    error: voucherTypesListError,
  } = useGetVouchers(organizationKeyName);

  const {
    data: punchCards,
    isLoading: punchCardsLoading,
    error: punchCardsError,
  } = usePunchCards(organizationKeyName);
  const productCategoriesOptions = (productCategories || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      label: o.name,
      value: o.id,
    }));

  productCategoriesOptions.unshift({
    label: "None",
    value: "",
  });

  const voucherOptions = (voucherTypesList || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((x) => ({
      label: x.name,
      value: x.id,
    }));

  voucherOptions.unshift({
    label: "None",
    value: "",
  });

  const punchCardsOptions = (punchCards || []).map((x: any) => ({
    label: x.name,
    value: x.id,
  }));

  punchCardsOptions.unshift({
    label: "None",
    value: "",
  });

  const isVoucherOffer = methods.watch("classification") === "voucher_type";
  const isVoucherStack = methods.watch("classification") === "product_pack";

  useHandleFetchError(productCategoriesError);
  useHandleFetchError(voucherTypesListError);
  useHandleFetchError(punchCardsError);

  if (
    isLoadingProductCategories ||
    isLoadingVoucherTypeList ||
    punchCardsLoading
  ) {
    return <SpinnerBlock />;
  }

  return (
    <>
      <FormRow>
        <TextInput
          name="name"
          placeholder="Enter product name"
          label="Product name"
          helperText="Only visible for admins. Helps you identify the product."
        />
        <TextInput
          name="title"
          placeholder="Enter display name"
          label="Display name"
          helperText="Visible for users in app, web and kiosks."
        />
      </FormRow>
      <TextAreaInput
        name="description"
        placeholder="Enter product description"
        rows={8}
        label="Product description"
        helperText="Visible for users in app, web and kiosks."
      />
      <SelectInput
        name="productCategoryId"
        label="Product Category"
        options={productCategoriesOptions}
        helperText="The product category will be used to group products in the app, web and kiosks."
      />
      <ProductTagsSelectField />
      {(isVoucherOffer || isVoucherStack) && (
        <FormRow>
          {isVoucherOffer && !isEdit && (
            <SelectInput
              label="Voucher"
              name="voucherTypeId"
              options={voucherOptions}
              helperText="Choose which voucher to use for this product."
            />
          )}
          {isVoucherStack && !isEdit && (
            <SelectInput
              label="Punch Card"
              name="productPackId"
              options={punchCardsOptions}
              helperText="Choose which punch card to use for this product."
            />
          )}
          <SelectInput
            label="Product cooldown ruleset ID"
            name="productCoolDownRulesetId"
            options={
              config.environment === "production"
                ? cooldownRulesets_PRODUCTION
                : cooldownRulesets_STAGING
            }
          />
        </FormRow>
      )}
    </>
  );
};
